import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useTheme } from '@/context/ThemeContext';
import { useAuth } from '@/context/AuthContext';
import { getFreshToken } from '@/lib/auth-token';
import AtividadeRecenteCard from './AtividadeRecenteCard';

interface SessionLogEntry {
  id: string;
  user_email: string;
  user_name: string;
  municipio_nome: string | null;
  action: string;
  user_agent: string | null;
  created_at: string;
}

const FIX_RLS_URL = `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/fix-rls-policies`;

interface TableInfo {
  name: string;
  count: number | null;
  status: 'ok' | 'error' | 'loading';
  error?: string;
}

interface SettingRow {
  key: string;
  value: string;
  label: string;
  updated_at?: string;
}

interface GlobalStats {
  totalFuncionarios: number;
  totalRegistosSiadap: number;
  totalProgressoes: number;
  totalPontos: number;
  mediaPontos: number;
  distribuicaoAvaliacoes: Record<string, number>;
  loading: boolean;
}

const TABLES_TO_CHECK = [
  { name: 'funcionarios',         label: 'Funcionários',            icon: 'ri-team-line'            },
  { name: 'progressoes',          label: 'Progressões',             icon: 'ri-arrow-up-circle-line' },
  { name: 'registos_siadap',      label: 'Registos SIADAP',         icon: 'ri-award-line'           },
  { name: 'historico_alteracoes', label: 'Histórico de Alterações', icon: 'ri-file-list-3-line'     },
  { name: 'unidades_organicas',   label: 'Unidades Orgânicas',      icon: 'ri-building-2-line'      },
  { name: 'historico_progressoes',label: 'Histórico Progressões',   icon: 'ri-history-line'         },
  { name: 'profiles',             label: 'Utilizadores',            icon: 'ri-user-line'            },
  { name: 'app_settings',         label: 'Configurações',           icon: 'ri-settings-3-line'      },
];

const AVALIACAO_COLORS: Record<string, string> = {
  Excelente:   'bg-emerald-400',
  'Muito Bom': 'bg-teal-400',
  Bom:         'bg-amber-400',
  Regular:     'bg-orange-400',
  Inadequado:  'bg-red-400',
  Relevante:   'bg-teal-300',
  Adequado:    'bg-zinc-300',
};

function timeAgo(dateStr?: string): string {
  if (!dateStr) return '—';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'agora mesmo';
  if (mins < 60) return `há ${mins} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `há ${hours}h`;
  const days = Math.floor(hours / 24);
  return `há ${days}d`;
}

export default function DiagnosticoTab() {
  const { theme, reloadTheme } = useTheme();
  const { isSuperAdmin } = useAuth();
  const [tables, setTables] = useState<TableInfo[]>(
    TABLES_TO_CHECK.map((t) => ({ name: t.name, count: null, status: 'loading' }))
  );
  const [settings, setSettings] = useState<SettingRow[]>([]);
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [dbLatency, setDbLatency] = useState<number | null>(null);
  const [dbStatus, setDbStatus] = useState<'checking' | 'ok' | 'error'>('checking');
  const [lastCheck, setLastCheck] = useState<Date | null>(null);
  const [themeJson, setThemeJson] = useState('');
  const [showThemeJson, setShowThemeJson] = useState(false);
  const [resyncing, setResyncing] = useState(false);
  const [resyncMsg, setResyncMsg] = useState('');
  const [fixingRls, setFixingRls] = useState(false);
  const [rlsMsg, setRlsMsg] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  const [rlsSql, setRlsSql] = useState<string | null>(null);
  const [sessionLogs, setSessionLogs] = useState<SessionLogEntry[]>([]);
  const [loadingSessionLogs, setLoadingSessionLogs] = useState(false);
  const [sessionLogsFilter, setSessionLogsFilter] = useState<'all' | 'login' | 'logout'>('all');
  const [sessionLogsSearch, setSessionLogsSearch] = useState('');
  const [globalStats, setGlobalStats] = useState<GlobalStats>({
    totalFuncionarios: 0, totalRegistosSiadap: 0, totalProgressoes: 0,
    totalPontos: 0, mediaPontos: 0, distribuicaoAvaliacoes: {}, loading: true,
  });

  const loadGlobalStats = useCallback(async () => {
    setGlobalStats((prev) => ({ ...prev, loading: true }));
    try {
      // Funcionários + pontos
      const { data: funcs } = await supabase
        .from('funcionarios')
        .select('pontos_atuais');
      const totalFuncionarios = funcs?.length ?? 0;
      const totalPontos = funcs?.reduce((s, f) => s + (f.pontos_atuais ?? 0), 0) ?? 0;
      const mediaPontos = totalFuncionarios > 0 ? totalPontos / totalFuncionarios : 0;

      // Registos SIADAP — distribuição por avaliação
      const { data: siadap } = await supabase
        .from('registos_siadap')
        .select('avaliacao');
      const totalRegistosSiadap = siadap?.length ?? 0;
      const dist: Record<string, number> = {};
      siadap?.forEach((r) => {
        dist[r.avaliacao] = (dist[r.avaliacao] ?? 0) + 1;
      });

      // Progressões
      const { count: totalProgressoes } = await supabase
        .from('progressoes')
        .select('*', { count: 'exact', head: true });

      setGlobalStats({
        totalFuncionarios,
        totalRegistosSiadap,
        totalProgressoes: totalProgressoes ?? 0,
        totalPontos,
        mediaPontos,
        distribuicaoAvaliacoes: dist,
        loading: false,
      });
    } catch {
      setGlobalStats((prev) => ({ ...prev, loading: false }));
    }
  }, []);

  const runDiagnostic = useCallback(async () => {
    setDbStatus('checking');
    setTables(TABLES_TO_CHECK.map((t) => ({ name: t.name, count: null, status: 'loading' })));
    setLoadingSettings(true);

    const t0 = performance.now();
    try {
      await supabase.from('app_settings').select('key').limit(1);
      setDbLatency(Math.round(performance.now() - t0));
      setDbStatus('ok');
    } catch {
      setDbStatus('error');
      setDbLatency(null);
    }

    const results = await Promise.all(
      TABLES_TO_CHECK.map(async (t) => {
        try {
          const { count, error } = await supabase
            .from(t.name as 'funcionarios')
            .select('*', { count: 'exact', head: true });
          if (error) return { name: t.name, count: null, status: 'error' as const, error: error.message };
          return { name: t.name, count: count ?? 0, status: 'ok' as const };
        } catch (err) {
          return { name: t.name, count: null, status: 'error' as const, error: String(err) };
        }
      })
    );
    setTables(results);

    const { data } = await supabase.from('app_settings').select('*').order('key');
    setSettings(data ?? []);
    setLoadingSettings(false);
    setLastCheck(new Date());
  }, []);

  const handleFixRls = useCallback(async () => {
    setFixingRls(true);
    setRlsMsg(null);
    setRlsSql(null);
    try {
      const token = await getFreshToken();
      const res = await fetch(FIX_RLS_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (json.sql) {
        // Management API não disponível — mostrar SQL para copiar
        setRlsSql(json.sql);
        setRlsMsg({ type: 'info', text: 'Copia o SQL abaixo e executa-o no Supabase SQL Editor para aplicar as restrições.' });
      } else if (json.success) {
        setRlsMsg({ type: 'success', text: 'Políticas RLS atualizadas com sucesso! Os dados estão agora restritos por módulo.' });
      } else {
        setRlsMsg({ type: 'error', text: json.error ?? 'Erro desconhecido ao aplicar RLS.' });
      }
    } catch (err) {
      setRlsMsg({ type: 'error', text: err instanceof Error ? err.message : 'Erro de rede.' });
    } finally {
      setFixingRls(false);
    }
  }, []);

  const handleResync = useCallback(async () => {
    setResyncing(true);
    setResyncMsg('');
    try {
      await reloadTheme();
      setResyncMsg('Tema re-sincronizado com sucesso!');
    } catch {
      setResyncMsg('Erro ao re-sincronizar o tema.');
    } finally {
      setResyncing(false);
      setTimeout(() => setResyncMsg(''), 4000);
    }
  }, [reloadTheme]);

  const loadSessionLogs = useCallback(async () => {
    setLoadingSessionLogs(true);
    const { data } = await supabase
      .from('session_logs')
      .select('id, user_email, user_name, municipio_nome, action, user_agent, created_at')
      .order('created_at', { ascending: false })
      .limit(100);
    setSessionLogs(data ?? []);
    setLoadingSessionLogs(false);
  }, []);

  useEffect(() => {
    runDiagnostic();
    loadGlobalStats();
    if (isSuperAdmin) loadSessionLogs();
  }, [runDiagnostic, loadGlobalStats, loadSessionLogs, isSuperAdmin]);

  useEffect(() => {
    setThemeJson(JSON.stringify(theme, null, 2));
  }, [theme]);

  const allOk = tables.every((t) => t.status === 'ok');
  const hasErrors = tables.some((t) => t.status === 'error');
  const totalAvaliacoes = Object.values(globalStats.distribuicaoAvaliacoes).reduce((s, v) => s + v, 0);

  return (
    <div className="space-y-6">

      {/* ── Estatísticas Globais ── */}
      <div className="bg-white rounded-2xl border border-zinc-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-zinc-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center bg-zinc-100 rounded-lg flex-shrink-0">
              <i className="ri-bar-chart-grouped-line text-zinc-600 text-sm" />
            </div>
            <div>
              <h2 className="text-[14px] font-semibold text-zinc-800">Estatísticas Globais</h2>
              <p className="text-[11px] text-zinc-400 mt-0.5">Totais consolidados do sistema</p>
            </div>
          </div>
          <button
            onClick={loadGlobalStats}
            className="whitespace-nowrap flex items-center gap-1.5 text-[12px] font-medium text-zinc-500 hover:text-zinc-800 transition-colors cursor-pointer"
          >
            <i className="ri-refresh-line text-sm" />
            Atualizar
          </button>
        </div>
        <div className="p-6">
          {globalStats.loading ? (
            <div className="flex items-center justify-center py-8">
              <i className="ri-loader-4-line text-2xl text-zinc-300 animate-spin" />
            </div>
          ) : (
            <>
              {/* KPIs */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                {[
                  { label: 'Funcionários', value: globalStats.totalFuncionarios, icon: 'ri-team-line', color: 'text-zinc-900' },
                  { label: 'Avaliações SIADAP', value: globalStats.totalRegistosSiadap, icon: 'ri-award-line', color: 'text-teal-700' },
                  { label: 'Progressões Registadas', value: globalStats.totalProgressoes, icon: 'ri-arrow-up-circle-line', color: 'text-emerald-700' },
                  { label: 'Média de Pontos', value: globalStats.mediaPontos.toFixed(1), icon: 'ri-bar-chart-line', color: 'text-amber-700', suffix: 'pts' },
                ].map((kpi) => (
                  <div key={kpi.label} className="bg-zinc-50 rounded-xl p-4 flex flex-col gap-2">
                    <div className="w-7 h-7 flex items-center justify-center">
                      <i className={`${kpi.icon} text-zinc-400 text-base`} />
                    </div>
                    <div>
                      <p className={`text-[22px] font-bold leading-tight ${kpi.color}`}>
                        {kpi.value}{kpi.suffix ? ` ${kpi.suffix}` : ''}
                      </p>
                      <p className="text-[11px] text-zinc-400 mt-0.5 leading-tight">{kpi.label}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Distribuição por avaliação */}
              {totalAvaliacoes > 0 && (
                <div>
                  <p className="text-[11px] font-semibold text-zinc-400 uppercase tracking-widest mb-3">
                    Distribuição por Avaliação ({totalAvaliacoes} registos)
                  </p>
                  {/* Barra empilhada */}
                  <div className="h-3 bg-zinc-100 rounded-full overflow-hidden flex mb-3">
                    {Object.entries(globalStats.distribuicaoAvaliacoes)
                      .sort(([, a], [, b]) => b - a)
                      .map(([av, count]) => (
                        <div
                          key={av}
                          className={`h-full transition-all ${AVALIACAO_COLORS[av] ?? 'bg-zinc-300'}`}
                          style={{ width: `${(count / totalAvaliacoes) * 100}%` }}
                          title={`${av}: ${count}`}
                        />
                      ))}
                  </div>
                  {/* Legenda */}
                  <div className="flex flex-wrap gap-3">
                    {Object.entries(globalStats.distribuicaoAvaliacoes)
                      .sort(([, a], [, b]) => b - a)
                      .map(([av, count]) => (
                        <div key={av} className="flex items-center gap-1.5">
                          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${AVALIACAO_COLORS[av] ?? 'bg-zinc-300'}`} />
                          <span className="text-[11px] text-zinc-600 font-medium">{av}</span>
                          <span className="text-[11px] text-zinc-400">{count}</span>
                          <span className="text-[10px] text-zinc-300">({((count / totalAvaliacoes) * 100).toFixed(0)}%)</span>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Atividade Recente Unificada ── */}
      <AtividadeRecenteCard />

      {/* ── Estado da Ligação ── */}
      <div className="bg-white rounded-2xl border border-zinc-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-zinc-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center bg-zinc-100 rounded-lg flex-shrink-0">
              <i className="ri-database-2-line text-zinc-600 text-sm" />
            </div>
            <div>
              <h2 className="text-[14px] font-semibold text-zinc-800">Estado da Ligação</h2>
              <p className="text-[11px] text-zinc-400 mt-0.5">
                {lastCheck ? `Última verificação: ${lastCheck.toLocaleTimeString('pt-PT')}` : 'A verificar...'}
              </p>
            </div>
          </div>
          <button
            onClick={runDiagnostic}
            className="whitespace-nowrap flex items-center gap-1.5 text-[12px] font-medium text-zinc-600 border border-zinc-200 bg-white hover:bg-zinc-50 px-3 py-2 rounded-lg transition-colors cursor-pointer"
          >
            <i className="ri-refresh-line text-sm" />
            Verificar agora
          </button>
        </div>

        <div className="p-6">
          {/* Status geral */}
          <div className={`flex items-center gap-4 p-4 rounded-xl border mb-5 ${
            dbStatus === 'ok' ? 'bg-emerald-50 border-emerald-100' :
            dbStatus === 'error' ? 'bg-red-50 border-red-100' :
            'bg-zinc-50 border-zinc-100'
          }`}>
            <div className={`w-10 h-10 flex items-center justify-center rounded-full flex-shrink-0 ${
              dbStatus === 'ok' ? 'bg-emerald-100' :
              dbStatus === 'error' ? 'bg-red-100' : 'bg-zinc-100'
            }`}>
              {dbStatus === 'checking'
                ? <i className="ri-loader-4-line text-zinc-400 text-lg animate-spin" />
                : dbStatus === 'ok'
                  ? <i className="ri-checkbox-circle-fill text-emerald-500 text-lg" />
                  : <i className="ri-error-warning-fill text-red-500 text-lg" />}
            </div>
            <div className="flex-1">
              <p className={`text-[13px] font-bold ${
                dbStatus === 'ok' ? 'text-emerald-800' :
                dbStatus === 'error' ? 'text-red-800' : 'text-zinc-600'
              }`}>
                {dbStatus === 'checking' ? 'A verificar ligação...' :
                 dbStatus === 'ok' ? 'Base de dados ligada e operacional' :
                 'Erro na ligação à base de dados'}
              </p>
              <p className="text-[11px] text-zinc-400 mt-0.5">
                Supabase · {import.meta.env.VITE_PUBLIC_SUPABASE_URL?.replace('https://', '').split('.')[0] ?? '—'}
              </p>
            </div>
            {dbLatency !== null && (
              <div className="flex-shrink-0 text-right">
                <p className={`text-[18px] font-bold ${dbLatency < 200 ? 'text-emerald-600' : dbLatency < 500 ? 'text-amber-600' : 'text-red-600'}`}>
                  {dbLatency}ms
                </p>
                <p className="text-[10px] text-zinc-400">latência</p>
              </div>
            )}
          </div>

          {/* Tabelas */}
          <p className="text-[11px] font-semibold text-zinc-400 uppercase tracking-widest mb-3">Tabelas da Base de Dados</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {TABLES_TO_CHECK.map((t, idx) => {
              const info = tables[idx];
              return (
                <div key={t.name} className={`flex flex-col gap-2 p-3 rounded-xl border transition-colors ${
                  info.status === 'ok' ? 'bg-white border-zinc-100' :
                  info.status === 'error' ? 'bg-red-50 border-red-100' :
                  'bg-zinc-50 border-zinc-100'
                }`}>
                  <div className="flex items-center justify-between">
                    <div className="w-6 h-6 flex items-center justify-center">
                      <i className={`${t.icon} text-sm ${info.status === 'error' ? 'text-red-400' : 'text-zinc-400'}`} />
                    </div>
                    {info.status === 'loading'
                      ? <i className="ri-loader-4-line text-zinc-300 text-xs animate-spin" />
                      : info.status === 'ok'
                        ? <i className="ri-checkbox-circle-line text-emerald-400 text-xs" />
                        : <i className="ri-error-warning-line text-red-400 text-xs" />}
                  </div>
                  <div>
                    <p className="text-[11px] font-semibold text-zinc-600 leading-tight">{t.label}</p>
                    <p className={`text-[18px] font-bold leading-tight mt-0.5 ${
                      info.status === 'ok' ? 'text-zinc-900' :
                      info.status === 'error' ? 'text-red-500' : 'text-zinc-300'
                    }`}>
                      {info.status === 'loading' ? '—' :
                       info.status === 'error' ? '!' :
                       info.count?.toLocaleString('pt-PT') ?? '0'}
                    </p>
                    {info.status === 'error' && (
                      <p className="text-[9px] text-red-400 mt-0.5 leading-tight truncate" title={info.error}>{info.error}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {!tables.some((t) => t.status === 'loading') && (
            <div className={`mt-4 flex items-center gap-2 text-[12px] px-3 py-2 rounded-lg ${
              allOk ? 'bg-emerald-50 text-emerald-700' :
              hasErrors ? 'bg-red-50 text-red-700' : 'bg-zinc-50 text-zinc-600'
            }`}>
              <i className={`${allOk ? 'ri-checkbox-circle-fill' : 'ri-error-warning-fill'} text-sm`} />
              {allOk
                ? `Todas as ${tables.length} tabelas acessíveis e operacionais.`
                : `${tables.filter((t) => t.status === 'error').length} tabela(s) com erro de acesso.`}
            </div>
          )}
        </div>
      </div>

      {/* ── Tema Visual ── */}
      <div className="bg-white rounded-2xl border border-zinc-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-zinc-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center bg-zinc-100 rounded-lg flex-shrink-0">
              <i className="ri-paint-brush-line text-zinc-600 text-sm" />
            </div>
            <div>
              <h2 className="text-[14px] font-semibold text-zinc-800">Tema Visual</h2>
              <p className="text-[11px] text-zinc-400 mt-0.5">Re-sincronizar o tema com o servidor ou inspecionar o JSON atual</p>
            </div>
          </div>
          <button
            onClick={handleResync}
            disabled={resyncing}
            className="whitespace-nowrap flex items-center gap-1.5 text-[12px] font-medium bg-zinc-900 text-white px-3 py-2 rounded-lg hover:bg-zinc-800 transition-colors cursor-pointer disabled:opacity-60"
          >
            {resyncing
              ? <><i className="ri-loader-4-line animate-spin text-sm" />A re-sincronizar...</>
              : <><i className="ri-refresh-line text-sm" />Forçar Re-sincronização</>}
          </button>
        </div>

        {resyncMsg && (
          <div className={`mx-6 mt-4 flex items-center gap-2 text-[12px] px-3 py-2.5 rounded-lg ${
            resyncMsg.includes('sucesso') ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' : 'bg-red-50 text-red-700 border border-red-100'
          }`}>
            <i className={`${resyncMsg.includes('sucesso') ? 'ri-checkbox-circle-fill' : 'ri-error-warning-fill'} text-sm`} />
            {resyncMsg}
          </div>
        )}

        <div className="p-6">
          {/* Mini preview do tema atual */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
            {[
              { label: 'Sidebar', value: theme.sidebarBg, type: 'color' },
              { label: 'Destaque', value: theme.sidebarAccent, type: 'color' },
              { label: 'Fundo', value: theme.pageBg, type: 'color' },
              { label: 'Primária', value: theme.primaryColor, type: 'color' },
            ].map((item) => (
              <div key={item.label} className="flex items-center gap-2.5 px-3 py-2.5 bg-zinc-50 rounded-xl">
                <div className="w-6 h-6 rounded-md border border-zinc-200 flex-shrink-0" style={{ backgroundColor: item.value }} />
                <div>
                  <p className="text-[10px] text-zinc-400 font-medium">{item.label}</p>
                  <p className="text-[11px] font-mono text-zinc-700">{item.value}</p>
                </div>
              </div>
            ))}
          </div>

          <button
            onClick={() => setShowThemeJson((v) => !v)}
            className="w-full flex items-center justify-between px-3 py-2.5 bg-zinc-50 rounded-xl hover:bg-zinc-100 transition-colors cursor-pointer"
          >
            <div className="flex items-center gap-2">
              <i className="ri-code-line text-zinc-400 text-sm" />
              <span className="text-[12px] font-medium text-zinc-600">Ver JSON completo</span>
              <span className="text-[11px] text-zinc-400">({Object.keys(theme).length} propriedades)</span>
            </div>
            <i className={`${showThemeJson ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-zinc-400`} />
          </button>

          {showThemeJson && (
            <div className="mt-3">
              <div className="flex items-center justify-between mb-2">
                <span className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-100 px-2 py-0.5 rounded-full">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse inline-block" />
                  Em memória
                </span>
                <button
                  onClick={() => navigator.clipboard.writeText(themeJson)}
                  className="whitespace-nowrap flex items-center gap-1 text-[11px] text-zinc-500 hover:text-zinc-800 transition-colors cursor-pointer"
                >
                  <i className="ri-clipboard-line text-xs" />
                  Copiar
                </button>
              </div>
              <pre className="bg-zinc-950 text-zinc-100 text-[11px] font-mono p-4 rounded-xl overflow-x-auto leading-relaxed max-h-72 overflow-y-auto">
                {themeJson}
              </pre>
            </div>
          )}
        </div>
      </div>

      {/* ── Configurações Guardadas ── */}
      <div className="bg-white rounded-2xl border border-zinc-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-zinc-100 flex items-center gap-3">
          <div className="w-8 h-8 flex items-center justify-center bg-zinc-100 rounded-lg flex-shrink-0">
            <i className="ri-settings-3-line text-zinc-600 text-sm" />
          </div>
          <div>
            <h2 className="text-[14px] font-semibold text-zinc-800">Configurações Guardadas</h2>
            <p className="text-[11px] text-zinc-400 mt-0.5">Todos os valores atualmente persistidos na base de dados</p>
          </div>
        </div>
        <div className="p-6">
          {loadingSettings ? (
            <div className="flex items-center justify-center py-8">
              <i className="ri-loader-4-line text-2xl text-zinc-300 animate-spin" />
            </div>
          ) : settings.length === 0 ? (
            <p className="text-[13px] text-zinc-400 text-center py-6">Nenhuma configuração encontrada.</p>
          ) : (
            <div className="space-y-1">
              {settings.map((s) => {
                const isTheme = s.key === 'app_theme';
                let displayValue = s.value;
                if (isTheme) {
                  try {
                    const parsed = JSON.parse(s.value || '{}');
                    displayValue = `{ tema — ${parsed?.nomeCamara ?? '—'} · ${parsed?.sidebarBg ?? ''} }`;
                  } catch { displayValue = '{ tema visual }'; }
                }
                return (
                  <div key={s.key} className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg hover:bg-zinc-50 transition-colors group">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-mono text-[10px] text-zinc-400 bg-zinc-100 px-1.5 py-0.5 rounded flex-shrink-0">{s.key}</span>
                      {s.label && <span className="text-[11px] text-zinc-500 truncate">{s.label}</span>}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className={`text-[11px] font-semibold truncate max-w-[200px] ${isTheme ? 'text-zinc-400 italic' : 'text-zinc-700'}`}>
                        {displayValue}
                      </span>
                      {s.updated_at && (
                        <span className="text-[10px] text-zinc-300 hidden group-hover:inline whitespace-nowrap">{timeAgo(s.updated_at)}</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ── Logs de Sessão (super_admin) ── */}
      {isSuperAdmin && (
        <div className="bg-white rounded-2xl border border-zinc-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-zinc-100 flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 flex items-center justify-center bg-zinc-100 rounded-lg flex-shrink-0">
                <i className="ri-login-circle-line text-zinc-600 text-sm" />
              </div>
              <div>
                <h2 className="text-[14px] font-semibold text-zinc-800">Logs de Sessão</h2>
                <p className="text-[11px] text-zinc-400 mt-0.5">Últimos 100 acessos de todos os utilizadores</p>
              </div>
            </div>
            <button
              onClick={loadSessionLogs}
              disabled={loadingSessionLogs}
              className="whitespace-nowrap flex items-center gap-1.5 text-[12px] font-medium text-zinc-600 border border-zinc-200 bg-white hover:bg-zinc-50 px-3 py-2 rounded-lg transition-colors cursor-pointer disabled:opacity-50"
            >
              <i className={`ri-refresh-line text-sm ${loadingSessionLogs ? 'animate-spin' : ''}`} />
              Atualizar
            </button>
          </div>

          {/* Export CSV */}
          {sessionLogs.length > 0 && (
            <div className="px-6 py-2 border-b border-zinc-50 flex justify-end">
              <button
                onClick={() => {
                  const filtered = sessionLogs.filter((l) => {
                    const matchFilter = sessionLogsFilter === 'all' || l.action === sessionLogsFilter;
                    const matchSearch = !sessionLogsSearch || l.user_email?.toLowerCase().includes(sessionLogsSearch.toLowerCase()) || l.municipio_nome?.toLowerCase().includes(sessionLogsSearch.toLowerCase()) || l.user_name?.toLowerCase().includes(sessionLogsSearch.toLowerCase());
                    return matchFilter && matchSearch;
                  });
                  const header = ['Data/Hora', 'Utilizador', 'Email', 'Ação', 'Módulo', 'User Agent'];
                  const rows = filtered.map((l) => [
                    new Date(l.created_at).toLocaleString('pt-PT'),
                    l.user_name || '',
                    l.user_email || '',
                    l.action,
                    l.municipio_nome || '',
                    (l.user_agent || '').replace(/,/g, ';'),
                  ]);
                  const csv = [header, ...rows].map((r) => r.map((v) => `"${v}"`).join(',')).join('\n');
                  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `logs-sessao-${new Date().toISOString().slice(0, 10)}.csv`;
                  a.click();
                  URL.revokeObjectURL(url);
                }}
                className="whitespace-nowrap flex items-center gap-1.5 text-[12px] font-medium text-zinc-500 hover:text-zinc-800 border border-zinc-200 bg-white hover:bg-zinc-50 px-3 py-1.5 rounded-lg transition-colors cursor-pointer"
              >
                <i className="ri-download-2-line text-sm" />
                Exportar CSV
              </button>
            </div>
          )}

          {/* Filtros */}
          <div className="px-6 py-3 border-b border-zinc-50 flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-1 bg-zinc-100 rounded-lg p-1">
              {(['all', 'login', 'logout'] as const).map((f) => (
                <button
                  key={f}
                  onClick={() => setSessionLogsFilter(f)}
                  className={`whitespace-nowrap px-3 py-1 text-[12px] font-medium rounded-md transition-all cursor-pointer ${
                    sessionLogsFilter === f ? 'bg-white text-zinc-800 shadow-sm' : 'text-zinc-500 hover:text-zinc-700'
                  }`}
                >
                  {f === 'all' ? 'Todos' : f === 'login' ? 'Logins' : 'Logouts'}
                </button>
              ))}
            </div>
            <div className="relative flex-1 min-w-[180px]">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 text-sm" />
              <input
                type="text"
                value={sessionLogsSearch}
                onChange={(e) => setSessionLogsSearch(e.target.value)}
                placeholder="Pesquisar por email ou módulo..."
                className="w-full pl-8 pr-3 py-1.5 text-[12px] border border-zinc-200 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-zinc-300"
              />
            </div>
            {sessionLogs.length > 0 && (
              <span className="text-[11px] text-zinc-400 flex-shrink-0">
                {sessionLogs.filter((l) => {
                  const matchFilter = sessionLogsFilter === 'all' || l.action === sessionLogsFilter;
                  const matchSearch = !sessionLogsSearch || l.user_email?.toLowerCase().includes(sessionLogsSearch.toLowerCase()) || l.municipio_nome?.toLowerCase().includes(sessionLogsSearch.toLowerCase()) || l.user_name?.toLowerCase().includes(sessionLogsSearch.toLowerCase());
                  return matchFilter && matchSearch;
                }).length} registos
              </span>
            )}
          </div>

          <div className="divide-y divide-zinc-50 max-h-[480px] overflow-y-auto">
            {loadingSessionLogs ? (
              <div className="flex items-center justify-center py-10">
                <i className="ri-loader-4-line text-2xl text-zinc-300 animate-spin" />
              </div>
            ) : (() => {
              const filtered = sessionLogs.filter((l) => {
                const matchFilter = sessionLogsFilter === 'all' || l.action === sessionLogsFilter;
                const matchSearch = !sessionLogsSearch
                  || l.user_email?.toLowerCase().includes(sessionLogsSearch.toLowerCase())
                  || l.municipio_nome?.toLowerCase().includes(sessionLogsSearch.toLowerCase())
                  || l.user_name?.toLowerCase().includes(sessionLogsSearch.toLowerCase());
                return matchFilter && matchSearch;
              });
              if (filtered.length === 0) {
                return (
                  <div className="flex flex-col items-center justify-center py-10 gap-2 text-zinc-400">
                    <div className="w-10 h-10 flex items-center justify-center bg-zinc-50 rounded-full">
                      <i className="ri-history-line text-xl" />
                    </div>
                    <p className="text-[13px]">Nenhum log encontrado.</p>
                  </div>
                );
              }
              return filtered.map((log) => (
                <div key={log.id} className="flex items-center justify-between px-6 py-3 hover:bg-zinc-50/60 transition-colors gap-4">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-7 h-7 flex items-center justify-center rounded-lg flex-shrink-0 ${
                      log.action === 'login' ? 'bg-emerald-50' : 'bg-zinc-50'
                    }`}>
                      <i className={`text-xs ${
                        log.action === 'login' ? 'ri-login-circle-line text-emerald-500' : 'ri-logout-circle-line text-zinc-400'
                      }`} />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[13px] font-medium text-zinc-800 truncate">
                          {log.user_name || log.user_email}
                        </span>
                        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full flex-shrink-0 ${
                          log.action === 'login' ? 'bg-emerald-100 text-emerald-700' : 'bg-zinc-100 text-zinc-500'
                        }`}>
                          {log.action === 'login' ? 'login' : 'logout'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                        <span className="text-[11px] text-zinc-400 truncate">{log.user_email}</span>
                        {log.municipio_nome && (
                          <>
                            <span className="text-zinc-200 text-[10px]">·</span>
                            <span className="text-[11px] text-zinc-400 flex items-center gap-1">
                              <i className="ri-building-4-line text-xs" />
                              {log.municipio_nome}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-[12px] font-medium text-zinc-500">{timeAgo(log.created_at)}</p>
                    <p className="text-[10px] text-zinc-300 mt-0.5">
                      {new Date(log.created_at).toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                      {' '}
                      {new Date(log.created_at).toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              ));
            })()}
          </div>
        </div>
      )}

      {/* ── Segurança RLS ── */}
      {isSuperAdmin && (
        <div className="bg-white rounded-2xl border border-amber-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-amber-100 flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 flex items-center justify-center bg-amber-50 rounded-lg flex-shrink-0">
                <i className="ri-shield-check-line text-amber-600 text-sm" />
              </div>
              <div>
                <h2 className="text-[14px] font-semibold text-zinc-800">Segurança — Row Level Security</h2>
                <p className="text-[11px] text-zinc-400 mt-0.5">
                  Garante que cada utilizador só acede aos dados do seu módulo
                </p>
              </div>
            </div>
            <button
              onClick={handleFixRls}
              disabled={fixingRls}
              className="whitespace-nowrap flex items-center gap-2 px-4 py-2 bg-amber-600 text-white text-[13px] font-medium rounded-lg hover:bg-amber-700 transition-colors cursor-pointer disabled:opacity-60"
            >
              {fixingRls
                ? <><i className="ri-loader-4-line animate-spin text-sm" />A aplicar...</>
                : <><i className="ri-shield-keyhole-line text-sm" />Aplicar Restrições RLS</>
              }
            </button>
          </div>

          <div className="p-6 space-y-4">
            {/* Explicação */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {[
                { icon: 'ri-user-forbid-line', title: 'Isolamento por módulo', desc: 'Utilizadores de um módulo não conseguem ver dados de outro módulo, mesmo que tentem aceder diretamente.' },
                { icon: 'ri-shield-star-line', title: 'Super Admin irrestrito', desc: 'O super_admin continua a ter acesso total a todos os módulos para gestão e diagnóstico.' },
                { icon: 'ri-lock-2-line', title: 'Proteção na base de dados', desc: 'As restrições são aplicadas diretamente no Supabase, não apenas no frontend.' },
              ].map((item) => (
                <div key={item.title} className="flex gap-3 p-3 bg-amber-50/50 rounded-xl border border-amber-100">
                  <div className="w-8 h-8 flex items-center justify-center bg-amber-100 rounded-lg flex-shrink-0 mt-0.5">
                    <i className={`${item.icon} text-amber-600 text-sm`} />
                  </div>
                  <div>
                    <p className="text-[12px] font-semibold text-zinc-700">{item.title}</p>
                    <p className="text-[11px] text-zinc-500 mt-0.5 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Tabelas afetadas */}
            <div>
              <p className="text-[11px] font-semibold text-zinc-400 uppercase tracking-widest mb-2">Tabelas protegidas</p>
              <div className="flex flex-wrap gap-2">
                {['funcionarios', 'registos_siadap', 'historico_progressoes', 'historico_alteracoes', 'progressoes', 'unidades_organicas', 'app_settings'].map((t) => (
                  <span key={t} className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-zinc-100 text-zinc-600 text-[11px] font-mono rounded-full">
                    <i className="ri-table-line text-xs" />
                    {t}
                  </span>
                ))}
              </div>
            </div>

            {/* Feedback */}
            {rlsMsg && (
              <div className={`flex items-start gap-3 p-3.5 rounded-xl border ${
                rlsMsg.type === 'success' ? 'bg-emerald-50 border-emerald-200' :
                rlsMsg.type === 'error' ? 'bg-red-50 border-red-200' :
                'bg-sky-50 border-sky-200'
              }`}>
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className={`text-sm ${
                    rlsMsg.type === 'success' ? 'ri-checkbox-circle-fill text-emerald-500' :
                    rlsMsg.type === 'error' ? 'ri-error-warning-fill text-red-500' :
                    'ri-information-fill text-sky-500'
                  }`} />
                </div>
                <p className={`text-[12px] ${
                  rlsMsg.type === 'success' ? 'text-emerald-700' :
                  rlsMsg.type === 'error' ? 'text-red-700' : 'text-sky-700'
                }`}>{rlsMsg.text}</p>
              </div>
            )}

            {/* SQL para copiar (fallback) */}
            {rlsSql && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[11px] font-semibold text-zinc-500 uppercase tracking-widest">SQL para executar no Supabase</p>
                  <button
                    onClick={() => navigator.clipboard.writeText(rlsSql)}
                    className="whitespace-nowrap flex items-center gap-1 text-[11px] text-zinc-500 hover:text-zinc-800 transition-colors cursor-pointer"
                  >
                    <i className="ri-clipboard-line text-xs" />
                    Copiar SQL
                  </button>
                </div>
                <pre className="bg-zinc-950 text-zinc-100 text-[10px] font-mono p-4 rounded-xl overflow-x-auto leading-relaxed max-h-64 overflow-y-auto">
                  {rlsSql}
                </pre>
                <p className="text-[11px] text-zinc-400 mt-2">
                  Vai ao <strong>Supabase Dashboard</strong> → <strong>SQL Editor</strong> → cola e executa o SQL acima.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Informação do Ambiente ── */}
      <div className="bg-white rounded-2xl border border-zinc-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-zinc-100 flex items-center gap-3">
          <div className="w-8 h-8 flex items-center justify-center bg-zinc-100 rounded-lg flex-shrink-0">
            <i className="ri-information-line text-zinc-600 text-sm" />
          </div>
          <h2 className="text-[14px] font-semibold text-zinc-800">Informação do Ambiente</h2>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {[
              { label: 'Supabase URL', value: import.meta.env.VITE_PUBLIC_SUPABASE_URL ?? '—', mono: true },
              { label: 'Anon Key (início)', value: (import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY ?? '—').slice(0, 20) + '...', mono: true },
              { label: 'Modo', value: import.meta.env.MODE ?? '—', mono: false },
              { label: 'Data/Hora atual', value: new Date().toLocaleString('pt-PT'), mono: false },
              { label: 'User Agent', value: navigator.userAgent.split(' ').slice(-2).join(' '), mono: false },
              { label: 'Idioma do browser', value: navigator.language, mono: false },
            ].map((item) => (
              <div key={item.label} className="flex items-start justify-between gap-3 px-3 py-2.5 rounded-lg bg-zinc-50">
                <span className="text-[11px] font-semibold text-zinc-500 flex-shrink-0">{item.label}</span>
                <span className={`text-[11px] text-zinc-700 text-right break-all ${item.mono ? 'font-mono' : ''}`}>{item.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

    </div>
  );
}
